import React, { useState } from "react";
import useSWR from "swr";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, Spinner, StatusBadge, Empty } from "@/components/Atoms";
import { fmtRel } from "@/lib/format";

const fetcher = (u) => api.get(u).then((r) => r.data);

export function JobActions({ job, onDone, size = "sm" }) {
    const [busy, setBusy] = useState(false);
    const canCancel = job.status === "queued" || job.status === "running";
    const canRetry = job.status === "failed" || job.status === "canceled";
    if (!canCancel && !canRetry) return null;

    const act = async (kind) => {
        if (kind === "cancel" && !window.confirm("Really cancel this job?")) return;
        setBusy(true);
        try {
            await api.post(`/api/admin/jobs/${job.id}/${kind}`);
            toast.success(kind === "cancel" ? "Job canceled" : "Job queued for retry");
            onDone && onDone();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    const cls =
        size === "lg"
            ? "px-3 py-1.5 text-sm"
            : "px-2 py-1 text-xs";
    return (
        <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
            {canCancel && (
                <button
                    type="button"
                    disabled={busy}
                    data-testid={`job-cancel-${job.id}`}
                    onClick={() => act("cancel")}
                    className={`${cls} font-mono border border-cm-danger/50 text-cm-danger hover:bg-cm-danger/10 disabled:opacity-40`}
                >
                    Cancel
                </button>
            )}
            {canRetry && (
                <button
                    type="button"
                    disabled={busy}
                    data-testid={`job-retry-${job.id}`}
                    onClick={() => act("retry")}
                    className={`${cls} font-mono border border-[#CCFF00]/60 text-[#CCFF00] hover:bg-[#CCFF00]/10 disabled:opacity-40`}
                >
                    Retry
                </button>
            )}
        </div>
    );
}

export default function JobsPage() {
    const [status, setStatus] = useState("");
    const [type, setType] = useState("");
    const nav = useNavigate();
    const qs = new URLSearchParams();
    if (status) qs.set("status", status);
    if (type) qs.set("type", type);
    qs.set("limit", "200");

    const { data, isLoading, mutate } = useSWR(`/api/admin/jobs?${qs}`, fetcher, {
        refreshInterval: 2000,
    });
    const { data: stats } = useSWR("/api/admin/jobs/stats", fetcher, {
        refreshInterval: 2000,
    });

    return (
        <div data-testid="jobs-page">
            <PageHeader
                testid="jobs-header"
                title="Jobs"
                subtitle="Asynchronous provisioning, updates and deletions. Auto-refresh every 2s."
            />

            {stats && (
                <div className="px-4 sm:px-6 pt-4 grid grid-cols-5 gap-px bg-zinc-800">
                    {["queued", "running", "succeeded", "failed", "canceled"].map((s) => (
                        <div
                            key={s}
                            className="cm-card p-3"
                            data-testid={`jobs-stat-${s}`}
                        >
                            <div className="cm-label">{s}</div>
                            <div className="font-mono text-xl mt-1">{stats[s] || 0}</div>
                        </div>
                    ))}
                </div>
            )}

            <div className="px-4 sm:px-6 py-3 flex gap-2">
                <select
                    data-testid="jobs-status-filter"
                    className="cm-input max-w-[180px]"
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                >
                    <option value="">all statuses</option>
                    {["queued", "running", "succeeded", "failed", "canceled"].map((s) => (
                        <option key={s}>{s}</option>
                    ))}
                </select>
                <select
                    data-testid="jobs-type-filter"
                    className="cm-input max-w-[200px]"
                    value={type}
                    onChange={(e) => setType(e.target.value)}
                >
                    <option value="">all types</option>
                    <option>cloud.provision</option>
                    <option>cloud.update</option>
                    <option>cloud.delete</option>
                </select>
            </div>

            <div className="px-4 sm:px-6 pb-4 sm:pb-6">
                {isLoading ? (
                    <Spinner />
                ) : !data?.length ? (
                    <Empty title="No jobs yet" hint="Jobs appear when you provision, update or delete clouds." />
                ) : (
                    <div className="cm-card overflow-hidden">
                        <div className="cm-table-wrap">
                        <table className="cm-table">
                            <thead>
                                <tr>
                                    <th className="cm-th">Job</th>
                                    <th className="cm-th">Type</th>
                                    <th className="cm-th">Cloud</th>
                                    <th className="cm-th">Status</th>
                                    <th className="cm-th">Progress</th>
                                    <th className="cm-th">Step</th>
                                    <th className="cm-th">Started</th>
                                    <th className="cm-th">Finished</th>
                                    <th className="cm-th">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.map((j) => (
                                    <tr
                                        key={j.id}
                                        className="cm-tr"
                                        data-testid={`jobs-row-${j.id}`}
                                        onClick={() => nav(`/jobs/${j.id}`)}
                                    >
                                        <td className="cm-td">
                                            <Link
                                                to={`/jobs/${j.id}`}
                                                className="font-mono text-xs text-[#CCFF00]"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                {j.id.slice(0, 8)}…
                                            </Link>
                                        </td>
                                        <td className="cm-td font-mono text-xs">{j.type}</td>
                                        <td className="cm-td font-mono text-xs text-zinc-400">
                                            {j.cloud_id ? (
                                                <Link to={`/clouds/${j.cloud_id}`} className="text-[#CCFF00]">
                                                    {j.cloud_id.slice(0, 8)}…
                                                </Link>
                                            ) : (
                                                "—"
                                            )}
                                        </td>
                                        <td className="cm-td">
                                            <StatusBadge status={j.status} />
                                        </td>
                                        <td className="cm-td">
                                            <div className="flex items-center gap-2 font-mono text-xs">
                                                <div className="w-24 h-1 bg-zinc-800">
                                                    <div
                                                        className="h-full bg-[#CCFF00]"
                                                        style={{ width: `${j.progress || 0}%` }}
                                                    />
                                                </div>
                                                <span>{j.progress ?? 0}%</span>
                                            </div>
                                        </td>
                                        <td className="cm-td font-mono text-xs text-zinc-400">
                                            {j.current_step || "—"}
                                        </td>
                                        <td className="cm-td text-xs text-zinc-500">
                                            {j.started_at ? fmtRel(j.started_at) : "—"}
                                        </td>
                                        <td className="cm-td text-xs text-zinc-500">
                                            {j.finished_at ? fmtRel(j.finished_at) : "—"}
                                        </td>
                                        <td className="cm-td">
                                            <JobActions job={j} onDone={() => mutate()} />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
